import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Card from '@/components/ui/Card';
import MonthGrid from '@/components/calendar/MonthGrid';
import { calendarAPI, CalendarItem } from '@/services/calendarApi';
import { EVENT_TYPE_COLOR, EVENT_TYPE_LABELS } from '@/components/calendar/eventTypes';
import { useAuth } from '@/context/AuthContext';
import PlayerAddEventModal from '@/components/player/PlayerAddEventModal';
import PlayerEventDetail from '@/components/player/PlayerEventDetail';
import LoadingStatus from '@/components/ui/LoadingStatus';
import DelayedLoading from '@/components/ui/DelayedLoading';

type View = 'calendar' | 'list';
type Tab = 'upcoming' | 'past';

// List view loads a wider window: 3 months either side of today.
function listRange(now: Date): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  // Day 0 of (month + 4) = last day of the month 3 months ahead.
  const to = new Date(now.getFullYear(), now.getMonth() + 4, 0, 23, 59, 59);
  return { from: from.toISOString(), to: to.toISOString() };
}

const fmtFull = (d: Date) =>
  d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const fmtTime = (d: Date) =>
  d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

const EventRow: React.FC<{ item: CalendarItem; onClick: () => void }> = ({ item, onClick }) => {
  const start = new Date(item.start);
  const end = item.end ? new Date(item.end) : undefined;
  const today = isSameDay(start, new Date());

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-stretch gap-4 px-3 py-3 text-left rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
    >
      {/* Date block */}
      <div
        className={`flex w-16 shrink-0 flex-col items-center justify-center rounded-lg py-2 ${
          today
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
        }`}
      >
        <span className="text-xs font-medium uppercase">
          {start.toLocaleDateString(undefined, { weekday: 'short' })}
        </span>
        <span className="text-xl font-bold leading-tight">{start.getDate()}</span>
        <span className="text-xs">
          {start.toLocaleDateString(undefined, { month: 'short' })}
        </span>
      </div>

      {/* Type colour bar */}
      <span
        className={`w-1 shrink-0 rounded-full ${EVENT_TYPE_COLOR[item.type] || 'bg-gray-500'}`}
      />

      {/* Details */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-semibold text-gray-900 dark:text-white">{item.title}</h3>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium text-white ${
              EVENT_TYPE_COLOR[item.type] || 'bg-gray-500'
            }`}
          >
            {EVENT_TYPE_LABELS[item.type] || 'Other'}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm text-gray-600 dark:text-gray-400">
          <span>
            {fmtFull(start)} · {fmtTime(start)}
            {end && ` – ${fmtTime(end)}`}
          </span>
          {item.location && <span>📍 {item.location}</span>}
        </div>
      </div>
    </button>
  );
};

const PlayerCalendarPage: React.FC = () => {
  const { user } = useAuth();
  const [view, setView] = useState<View>('calendar');
  const [month, setMonth] = useState(new Date());
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('upcoming');
  const [addDay, setAddDay] = useState<Date | null>(null);
  const [detailItem, setDetailItem] = useState<CalendarItem | null>(null);
  const request = useRef(0);
  const hasLoaded = useRef(false);

  const load = useCallback(async () => {
    const attempt = ++request.current;
    setLoading(true);
    setError('');
    let from: string;
    let to: string;
    if (view === 'calendar') {
      from = new Date(month.getFullYear(), month.getMonth(), 1).toISOString();
      // Exclusive upper bound = first instant of next month.
      to = new Date(month.getFullYear(), month.getMonth() + 1, 1).toISOString();
    } else {
      ({ from, to } = listRange(new Date()));
    }
    try {
      const res = await calendarAPI.myFeed(from, to);
      if (attempt !== request.current) return;
      setItems(res.data || []);
      hasLoaded.current = true;
      setLoaded(true);
    } catch {
      if (attempt !== request.current) return;
      setError(hasLoaded.current ? 'Failed to refresh calendar' : 'Failed to load calendar');
      hasLoaded.current = true;
      setLoaded(true);
    } finally {
      if (attempt === request.current) setLoading(false);
    }
  }, [view, month]);

  useEffect(() => {
    void load();
    return () => { request.current += 1; };
  }, [load]);

  const shift = (n: number) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const upcomingItems = items
      .filter((it) => new Date(it.start).getTime() >= now)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    const pastItems = items
      .filter((it) => new Date(it.start).getTime() < now)
      .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
    return { upcoming: upcomingItems, past: pastItems };
  }, [items]);

  const list = tab === 'upcoming' ? upcoming : past;

  const viewBtn = (key: View, label: string) => (
    <button
      type="button"
      onClick={() => setView(key)}
      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
        view === key
          ? 'bg-blue-600 text-white'
          : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
    >
      {label}
    </button>
  );

  const tabBtn = (key: Tab, label: string, count: number) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        tab === key
          ? 'border-blue-600 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
      }`}
    >
      {label}
      <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{count}</span>
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Calendar</h1>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 divide-x divide-gray-300 dark:divide-gray-600">
            {viewBtn('calendar', 'Calendar')}
            {viewBtn('list', 'List')}
          </div>

          {view === 'calendar' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => shift(-1)}
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
              >
                ‹
              </button>
              <span className="font-medium w-40 text-center text-gray-900 dark:text-gray-100">
                {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </span>
              <button
                onClick={() => shift(1)}
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
              >
                ›
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setAddDay(new Date())}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            + Add
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <span>{error}</span>
          <button type="button" onClick={() => { void load(); }} className="font-semibold underline underline-offset-2">
            Retry calendar
          </button>
        </div>
      )}

      <Card>
        {loading && loaded && (
          <LoadingStatus variant="inline" label="Refreshing calendar" className="mb-3" />
        )}
        {view === 'calendar' ? (
          !loaded ? (
            <DelayedLoading loading={loading}>
              <LoadingStatus label="Loading calendar" className="justify-center py-8" />
            </DelayedLoading>
          ) : (
            <MonthGrid
              month={month}
              items={items}
              onDayClick={(d) => setAddDay(d)}
              onItemClick={(it) => setDetailItem(it)}
            />
          )
        ) : (
          <>
            <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
              {tabBtn('upcoming', 'Upcoming', upcoming.length)}
              {tabBtn('past', 'Past', past.length)}
            </div>

            {!loaded ? (
              <DelayedLoading loading={loading}>
                <LoadingStatus label="Loading calendar events" className="justify-center py-8" />
              </DelayedLoading>
            ) : list.length === 0 ? (
              <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                {tab === 'upcoming' ? 'No upcoming events.' : 'No past events.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800 pt-1">
                {list.map((it) => (
                  <EventRow
                    key={`${it.source}-${it.id}`}
                    item={it}
                    onClick={() => setDetailItem(it)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      {addDay !== null && (
        <PlayerAddEventModal
          day={addDay}
          onSaved={() => {
            setAddDay(null);
            load();
          }}
          onClose={() => setAddDay(null)}
        />
      )}

      {detailItem && (
        <PlayerEventDetail
          item={detailItem}
          currentUserId={user?.id}
          onClose={() => setDetailItem(null)}
          onDeleted={() => {
            setDetailItem(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default PlayerCalendarPage;
